import { motion } from "framer-motion";
import { C } from "../lib/colors";
import { Logo } from "../components/Logo";
import { SpinningO } from "../components/SpinningO";
import { IllustrationAgents } from "../components/illustrations/IllustrationAgents";
import { IllustrationPipeline } from "../components/illustrations/IllustrationPipeline";
import { IllustrationContent } from "../components/illustrations/IllustrationContent";
import { IllustrationClients } from "../components/illustrations/IllustrationClients";

// Shared scroll-reveal config. `once: true` so animations don't replay
// every time the user scrolls back over them. `margin: "-60px"` starts
// the animation slightly before the element enters the viewport, so by
// the time the user's eye lands on it the motion is already settling
// (feels snappier than reveal-on-fully-visible).
const inViewProps = {
  initial:    { opacity: 0, y: 28 },
  whileInView:{ opacity: 1, y: 0 },
  viewport:   { once: true, margin: "-60px" },
  transition: { duration: 0.55, ease: "easeOut" },
};

// Stagger pattern: parent declares staggerChildren, children use these
// variants to inherit the staggered timing automatically.
const staggerParent = {
  initial:   "hidden",
  whileInView:"visible",
  viewport:  { once: true, margin: "-60px" },
  variants:  { visible: { transition: { staggerChildren: 0.08 } } },
};
const staggerChild = {
  hidden:  { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

export const HowItWorksPage = ({ onNavigate, authRole }) => {
  const dashboardPage = authRole === "owner" ? "agents" : authRole === "client" ? "portal" : null;
  const dashboardLabel = authRole === "owner" ? "Dashboard →" : authRole === "client" ? "Portal →" : null;
  const steps = [
    {
      num:"01", icon:"✉", color:C.blue,
      title:"You sign up in minutes",
      sub:"No calls. No contracts. Just fill out a quick form.",
      desc:"Getting started takes less than 15 minutes. You answer a few questions about your business, your customers, and the topics you want to rank for. That's it — we handle everything from there.",
      promise:"No technical knowledge required. If you can describe your business, we can write about it.",
      ill:<IllustrationClients/>,
    },
    {
      num:"02", icon:"◈", color:C.purple,
      title:"We build your content strategy",
      sub:"A custom roadmap built around your business goals.",
      desc:"Our AI analyses your industry, your competitors, and the exact keywords your ideal customers are searching for. Within 24 hours, you'll have a clear content plan — blog topics, social ideas, and SEO keywords — tailored specifically to you.",
      promise:"You get to review and approve the strategy before a single word is written.",
      ill:<IllustrationPipeline/>,
    },
    {
      num:"03", icon:"✍", color:C.green,
      title:"We write and deliver your content",
      sub:"Professional, SEO-optimised content — ready to publish.",
      desc:"Our AI writers produce blog posts, social captions, and email newsletters that sound like you, rank on Google, and actually convert readers into customers. Every piece is reviewed for quality before it reaches your inbox.",
      promise:"Your first batch of content arrives within 48 hours of sign-up.",
      ill:<IllustrationContent/>,
    },
    {
      num:"04", icon:"◉", color:C.amber,
      title:"You review, approve, and publish",
      sub:"You stay in control — without doing the heavy lifting.",
      desc:"Everything lands in your private client portal. Review each piece, request any changes, and publish when you're happy. Most clients spend less than 30 minutes a month managing their content. The rest is handled for you.",
      promise:"Unlimited revisions within 7 days. If you're not happy, we rewrite it — free.",
      ill:<IllustrationAgents/>,
    },
  ];

  const included = [
    {icon:"✍", color:C.green,  title:"SEO Blog Posts",      desc:"Long-form articles written to rank on Google and answer the questions your customers are already searching for."},
    {icon:"◎", color:C.blue,   title:"Social Media Captions", desc:"Ready-to-post captions for Instagram, Facebook, LinkedIn, and X — just copy, paste, and publish."},
    {icon:"◈", color:C.purple, title:"Content Strategy",      desc:"A monthly roadmap of topics and keywords built around your business goals and what your competitors are missing."},
    {icon:"◧", color:C.amber,  title:"Your Client Portal",    desc:"A private dashboard where you can review, approve, and download all your content anytime you like."},
    {icon:"◉", color:C.cyan,   title:"Editorial Review",      desc:"Every piece is scored for readability and SEO strength before it's delivered to you — no low-quality content, ever."},
    {icon:"◷", color:C.pink,   title:"Monthly Reports",       desc:"Clear, plain-English reports showing how your content is performing — traffic, rankings, and what's working."},
  ];

  return (
    <div style={{background:"#000000",minHeight:"100vh",fontFamily:"'Instrument Sans',sans-serif",color:C.text}}>
      {/* NAV */}
      <nav style={{position:"sticky",top:0,zIndex:100,background:"rgba(0,0,0,0.95)",backdropFilter:"blur(20px)",
        borderBottom:`1px solid ${C.border}`,padding:"0 5%",height:96,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <button onClick={()=>onNavigate("landing")} style={{background:"none",border:"none",cursor:"pointer"}}><Logo size={44}/></button>
        <div style={{display:"flex",gap:12,alignItems:"center"}}>
          <button onClick={()=>onNavigate("landing")} style={{background:"none",border:"none",color:C.muted,fontSize:13,cursor:"pointer",fontFamily:"'Instrument Sans',sans-serif"}}>
            ← Back to Home
          </button>
          <button onClick={()=>onNavigate(dashboardPage || "onboarding")} style={{background:C.green,color:"#000",border:"none",borderRadius:9,
            padding:"9px 22px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Instrument Sans',sans-serif",transition:"all 0.2s"}}
            onMouseEnter={e=>e.target.style.background="#fff"} onMouseLeave={e=>e.target.style.background=C.green}>
            {dashboardLabel || "Get Started →"}
          </button>
        </div>
      </nav>

      {/* HERO */}
      <div style={{padding:"80px 5% 64px",textAlign:"center",position:"relative",overflow:"hidden",
        background:"linear-gradient(180deg,rgba(0,240,160,0.04),transparent)"}}>
        <div style={{position:"absolute",top:0,left:"50%",transform:"translateX(-50%)",
          width:600,height:400,borderRadius:"50%",
          background:"radial-gradient(circle,rgba(0,240,160,0.06) 0%,transparent 70%)",pointerEvents:"none"}}/>
        <div style={{display:"inline-flex",alignItems:"center",gap:10,background:"rgba(0,240,160,0.08)",
          border:"1px solid rgba(0,240,160,0.2)",color:C.green,padding:"6px 18px 6px 10px",borderRadius:100,
          fontSize:11,fontWeight:700,letterSpacing:1,textTransform:"uppercase",marginBottom:24,animation:"fadeUp 0.6s ease both"}}>
          <SpinningO size={32} mode="wobble"/>
          Media on Auto Pilot
        </div>
        <h1 style={{fontFamily:"'Sora',sans-serif",fontSize:"clamp(34px,5vw,62px)",fontWeight:800,
          lineHeight:1.08,letterSpacing:-2,maxWidth:760,margin:"0 auto 20px",animation:"fadeUp 0.6s 0.08s ease both"}}>
          Great content for your business —<br/><span style={{color:C.green}}>without the hard work.</span>
        </h1>
        <p style={{fontSize:18,color:C.muted,maxWidth:540,margin:"0 auto 36px",lineHeight:1.75,animation:"fadeUp 0.6s 0.16s ease both"}}>
          You tell us about your business once. We write, optimise, and deliver everything — month after month — while you focus on what you do best.
        </p>
        <div style={{display:"inline-flex",alignItems:"center",gap:0,background:C.s1,border:`1px solid ${C.border}`,
          borderRadius:100,padding:"10px 6px",animation:"fadeUp 0.6s 0.24s ease both"}}>
          {["Sign up","Get strategy","Content delivered","You publish"].map((l,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center"}}>
              <div style={{display:"flex",alignItems:"center",gap:7,padding:"4px 14px"}}>
                <div style={{width:20,height:20,borderRadius:"50%",background:`${C.green}20`,border:`1px solid ${C.green}50`,
                  display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:C.green,fontFamily:"'DM Mono',monospace",fontWeight:700,flexShrink:0}}>{i+1}</div>
                <span style={{fontSize:12,fontWeight:600,color:"rgba(255,255,255,0.75)",whiteSpace:"nowrap"}}>{l}</span>
              </div>
              {i<3&&<span style={{color:C.border2,fontSize:14,flexShrink:0}}>›</span>}
            </div>
          ))}
        </div>
      </div>

      {/* STEPS — each one slides in from the side it's anchored to */}
      <div style={{maxWidth:960,margin:"0 auto",padding:"20px 5% 80px",display:"flex",flexDirection:"column",gap:72}}>
        {steps.map((s,i)=>{
          const fromLeft = i%2===0;
          return (
            <motion.div key={i}
              initial={{ opacity: 0, x: fromLeft ? -40 : 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))",gap:48,alignItems:"center",
                direction:i%2===0?"ltr":"rtl"}}>
              <div style={{direction:"ltr"}}>
                <div style={{display:"inline-flex",alignItems:"center",gap:10,marginBottom:18}}>
                  <div style={{width:42,height:42,borderRadius:12,background:`${s.color}14`,border:`1px solid ${s.color}28`,
                    display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,color:s.color,flexShrink:0}}>{s.icon}</div>
                  <span style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:s.color,fontWeight:700,letterSpacing:2,textTransform:"uppercase"}}>Step {s.num}</span>
                </div>
                <h3 style={{fontFamily:"'Sora',sans-serif",fontSize:"clamp(22px,3vw,30px)",fontWeight:800,
                  letterSpacing:-0.8,marginBottom:8,lineHeight:1.15}}>{s.title}</h3>
                <div style={{fontSize:14,color:s.color,fontWeight:600,marginBottom:16,lineHeight:1.5}}>{s.sub}</div>
                <p style={{fontSize:15,color:"rgba(255,255,255,0.62)",lineHeight:1.8,marginBottom:20}}>{s.desc}</p>
                <div style={{display:"flex",alignItems:"flex-start",gap:10,background:`${s.color}07`,
                  border:`1px solid ${s.color}18`,borderRadius:10,padding:"12px 16px"}}>
                  <span style={{color:s.color,fontSize:15,flexShrink:0,marginTop:1}}>✓</span>
                  <span style={{fontSize:13,color:`rgba(255,255,255,0.55)`,lineHeight:1.6}}>{s.promise}</span>
                </div>
              </div>
              {/* Illustration card breathes gently via Framer's keyframe array
                  — replaces the old CSS `float` keyframe so all motion runs
                  through one engine and respects prefers-reduced-motion. */}
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: i * 0.6 }}
                style={{direction:"ltr",borderRadius:16,overflow:"hidden",border:`1px solid ${C.border}`,height:210}}>
                {s.ill}
              </motion.div>
            </motion.div>
          );
        })}
      </div>

      {/* WHAT'S INCLUDED */}
      <div style={{background:C.s1,borderTop:`1px solid ${C.border}`,borderBottom:`1px solid ${C.border}`,padding:"72px 5%"}}>
        <div style={{maxWidth:960,margin:"0 auto"}}>
          <motion.div {...inViewProps} style={{textAlign:"center",marginBottom:48}}>
            <div style={{fontSize:11,fontWeight:700,letterSpacing:2,textTransform:"uppercase",color:C.green,
              marginBottom:12,fontFamily:"'DM Mono',monospace"}}>What You Get</div>
            <h2 style={{fontFamily:"'Sora',sans-serif",fontSize:"clamp(24px,3.5vw,38px)",fontWeight:800,
              letterSpacing:-1,marginBottom:12}}>Everything included, every month.</h2>
            <p style={{color:C.muted,fontSize:15,maxWidth:480,margin:"0 auto",lineHeight:1.7}}>
              No add-ons. No surprises. Everything you need to stay visible, relevant, and competitive online.
            </p>
          </motion.div>
          <motion.div {...staggerParent}
            style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:16}}>
            {included.map((item,i)=>(
              <motion.div key={i} variants={staggerChild}
                whileHover={{ y: -4, borderColor: `${item.color}45` }}
                transition={{ type: "spring", stiffness: 300, damping: 22 }}
                style={{background:C.s2,border:`1px solid ${C.border}`,borderRadius:14,padding:22,
                  willChange:"transform"}}>
                <div style={{width:40,height:40,borderRadius:10,background:`${item.color}12`,border:`1px solid ${item.color}25`,
                  display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,color:item.color,marginBottom:14}}>{item.icon}</div>
                <div style={{fontFamily:"'Sora',sans-serif",fontSize:15,fontWeight:700,marginBottom:7,color:"#fff"}}>{item.title}</div>
                <div style={{fontSize:13,color:C.muted,lineHeight:1.65}}>{item.desc}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Testimonials + FAQ moved to LandingPage — they belong on the
          highest-traffic page where conversion happens, not buried mid-
          way through a long explainer flow. */}

      {/* CTA — scales into view + button has a tactile spring on hover/press */}
      <div style={{padding:"0 5% 80px"}}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.55, ease: "easeOut" }}
          style={{background:"linear-gradient(135deg,rgba(0,240,160,0.07),rgba(77,170,255,0.05))",
            border:`1px solid rgba(0,240,160,0.18)`,borderRadius:22,padding:"60px 48px",textAlign:"center",maxWidth:720,margin:"0 auto"}}>
          <h2 style={{fontFamily:"'Sora',sans-serif",fontSize:"clamp(24px,4vw,42px)",fontWeight:800,letterSpacing:-1.5,marginBottom:12}}>
            Ready to get started?
          </h2>
          <p style={{color:C.muted,fontSize:16,maxWidth:420,margin:"0 auto 32px",lineHeight:1.7}}>
            Fill out your onboarding form in 15 minutes. Your first content arrives in 48 hours.
          </p>
          <motion.button onClick={()=>onNavigate("onboarding")}
            whileHover={{ y: -3, scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            transition={{ type: "spring", stiffness: 400, damping: 22 }}
            style={{background:C.green,color:"#000",border:"none",borderRadius:11,
              padding:"15px 38px",fontSize:16,fontWeight:700,cursor:"pointer",fontFamily:"'Instrument Sans',sans-serif",
              display:"inline-flex",alignItems:"center",gap:10}}>
            Get Started →
          </motion.button>
          <div style={{fontSize:13,color:C.muted,marginTop:14,display:"flex",gap:20,justifyContent:"center",flexWrap:"wrap"}}>
            {["15-day trial for $97","Cancel anytime","First content in 48hrs","No long-term contracts"].map(p=>(
              <span key={p} style={{display:"flex",alignItems:"center",gap:5}}><span style={{color:C.green}}>✓</span>{p}</span>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};
